import { useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

/**
 * Render a user's Deal Template fields as form controls.
 * Props:
 *  - fields: [{id, key, label, field_type, required, options?}]
 *  - values: { [key]: value }
 *  - onChange: (nextValues) => void
 *  - idPrefix: string (for data-testids, e.g. "new-deal-custom")
 */
export const CustomFieldsRenderer = ({ fields, values, onChange, idPrefix = 'custom' }) => {
  if (!fields || fields.length === 0) return null;
  const set = (k, v) => onChange({ ...(values || {}), [k]: v });
  return (
    <div className="space-y-3 pt-3 mt-3 border-t border-slate-200" data-testid={`${idPrefix}-section`}>
      <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-500">Your Deal Profile</p>
      {fields.map((f) => {
        const v = values?.[f.key];
        const tid = `${idPrefix}-${f.key}`;
        return (
          <div key={f.id} data-testid={`${idPrefix}-wrapper-${f.key}`}>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
              {f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            {f.field_type === 'text' && (
              <Input value={v || ''} onChange={(e) => set(f.key, e.target.value)} className="mt-1 rounded-lg border-slate-200" data-testid={tid} />
            )}
            {f.field_type === 'number' && (
              <Input type="number" value={v ?? ''} onChange={(e) => set(f.key, e.target.value === '' ? null : Number(e.target.value))} className="mt-1 rounded-lg border-slate-200" data-testid={tid} />
            )}
            {f.field_type === 'currency' && (
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                <Input type="number" step="0.01" value={v ?? ''} onChange={(e) => set(f.key, e.target.value === '' ? null : Number(e.target.value))} className="pl-7 rounded-lg border-slate-200" data-testid={tid} />
              </div>
            )}
            {f.field_type === 'date' && (
              <Input type="date" value={v || ''} onChange={(e) => set(f.key, e.target.value)} className="mt-1 rounded-lg border-slate-200" data-testid={tid} />
            )}
            {f.field_type === 'select' && (
              <Select value={v || ''} onValueChange={(val) => set(f.key, val)}>
                <SelectTrigger className="mt-1 rounded-lg border-slate-200" data-testid={tid}>
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  {(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {f.field_type === 'checkbox' && (
              <label className="flex items-center gap-2 mt-1.5 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!v}
                  onChange={(e) => set(f.key, e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  data-testid={tid}
                />
                <span>Yes</span>
              </label>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Validate required custom fields. Returns first error label or null.
 */
export const validateCustomFields = (fields, values) => {
  for (const f of fields || []) {
    if (!f.required) continue;
    const v = values?.[f.key];
    const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
    if (empty && f.field_type !== 'checkbox') return `${f.label} is required`;
    if (empty && f.field_type === 'checkbox') return `${f.label} must be checked`;
  }
  return null;
};

// Suppress unused-var warning when only default component matters
export default CustomFieldsRenderer;
